import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../utils/AuthContext';
import LoginModal from '../components/LoginModal';
import SubscribeForm from '../components/SubscribeForm';
import SEO from '../components/SEO';
import './plans.css';

const PLANS = [
  {
    key: 'individual',
    icon: 'bi-person-check',
    title: 'Individual',
    subtitle: 'Full access to legislative tools',
    monthly: { price: '$14.99', label: '/month' },
    annual: { price: '$152.99', label: '/year', savings: 'Save 15%' },
    features: [
      'Personalized Dashboard with hearings & news highlights',
      'Legislation — search, filter & track bills in real time',
      'Bill Profiles — full details, analysis & voting history',
      'Staff Directory — legislators, committees & agency personnel',
      'Public Square — community posts & announcements',
      'Job Openings — staffing needs from legislators & agencies',
    ],
  },
  {
    key: 'workplace',
    icon: 'bi-people',
    title: 'Workspace',
    subtitle: 'Everything in Individual, plus team tools',
    monthly: { price: '$74.99', label: '/month' },
    annual: { price: '$764.99', label: '/year', savings: 'Save 15%' },
    features: [
      'All Individual features',
      'Workspaces — shared bill tracking across your team',
      'Client management & bill assignments',
      'Team admin controls & permission management',
      'Collaborative government relations workflows',
    ],
  },
];

const USER_TYPES = [
  {
    icon: 'bi-person-lines-fill',
    title: 'Lobbyists & Consultants',
    desc: 'Monitor multiple client portfolios, track bill activity across committees, and stay ahead of floor schedules — all from a single dashboard built for how you work.',
    tag: 'Individual or Workspace',
  },
  {
    icon: 'bi-building',
    title: 'Legislative & Agency Staff',
    desc: 'Access the full staff directory, follow bill profiles through every amendment, and start each morning with curated headlines — without jumping between a dozen tabs.',
    tag: 'Individual',
  },
  {
    icon: 'bi-briefcase-fill',
    title: 'Government Relations Teams',
    desc: 'Coordinate shared bill tracking, assign issues to team members, manage client relationships, and keep everyone aligned with Workspace collaboration tools.',
    tag: 'Workspace',
  },
  {
    icon: 'bi-megaphone-fill',
    title: 'Public Affairs & Advocacy',
    desc: 'Identify the right legislators, track bills that affect your issue areas, post announcements to the Sacramento policy community, and build your professional network.',
    tag: 'Individual or Workspace',
  },
  {
    icon: 'bi-building-check',
    title: 'Nonprofits & Associations',
    desc: 'Give your whole advocacy team access to real-time legislative intelligence. Track bills, coordinate engagement, and keep volunteers and staff on the same page.',
    tag: 'Workspace',
  },
  {
    icon: 'bi-person-badge',
    title: 'Board Members & Officers',
    desc: 'Stay informed about the legislation that affects your organization without needing a dedicated government affairs team. Get the context you need, when you need it.',
    tag: 'Individual',
  },
];

export default function PlansPage() {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();

  const [billing, setBilling] = useState('monthly');
  const [view, setView] = useState('plans');
  const [formPlan, setFormPlan] = useState('individual');
  const [formBilling, setFormBilling] = useState('monthly');

  const handlePlanCTA = (planKey) => {
    if (auth.isAuthenticated) {
      navigate(`/subscribe?plan=${planKey}`);
    } else {
      setFormPlan(planKey);
      setFormBilling(billing);
      setView('subscribe');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setView('plans');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (view === 'subscribe') {
    return (
      <>
        <div className="pg-subscribe-view">
          <div className="pg-container">
            <button className="pg-back-btn" onClick={handleBack}>
              <i className="bi bi-arrow-left" /> Back to Plans
            </button>
          </div>
          <SubscribeForm activePlan={formPlan} activeBilling={formBilling} />
          <div className="pg-subscribe-signin">
            <p>
              Already have an account?{' '}
              <button className="pg-auth-link" data-bs-toggle="modal" data-bs-target="#loginModal">
                Sign in
              </button>
            </p>
          </div>
        </div>
        <footer className="pg-footer">
          <span>&copy; <i>'ello</i>Govna {new Date().getFullYear()}</span>
          <a
            className="pg-footer-social"
            href="https://www.linkedin.com/company/ellogovna/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="'elloGovna on LinkedIn"
          >
            <i className="bi bi-linkedin" />
          </a>
        </footer>
        <LoginModal />
      </>
    );
  }

  return (
    <>
      <SEO
        title="Plans & Pricing | 'elloGovna"
        description="Compare 'elloGovna's Individual and Workplace plans for legislative tracking, staff directory access, and stakeholder engagement tools for California's Capitol community."
        path="/plans"
      />
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="pg-hero">
        <div className="pg-container">
          <p className="pg-hero-eyebrow">Plans &amp; Pricing</p>
          <h1 className="pg-hero-title">Unlock the Full Platform</h1>
          <p className="pg-hero-sub">
            Built for the Sacramento policy community — from independent lobbyists to full
            government relations teams. Choose the plan that fits how you work.
          </p>
          <p className="pg-hero-trial">
            <i className="bi bi-gift-fill" /> Try the full platform free for 14 days — no commitment required.
          </p>
        </div>
      </section>

      {/* ── Who it's for ─────────────────────────────────── */}
      <section className="pg-who">
        <div className="pg-container">
          <h2 className="pg-section-title">Who benefits from <i>'ello</i>Govna?</h2>
          <p className="pg-section-sub">
            Whether you're tracking bills solo or coordinating a team of government relations
            professionals, there's a plan built for your workflow.
          </p>
          <div className="pg-who-grid">
            {USER_TYPES.map(({ icon, title, desc, tag }) => (
              <div key={title} className="pg-who-card">
                <div className="pg-who-card-top">
                  <div className="pg-who-icon"><i className={`bi ${icon}`} /></div>
                  <span className="pg-who-tag">{tag}</span>
                </div>
                <h3 className="pg-who-title">{title}</h3>
                <p className="pg-who-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plans ────────────────────────────────────────── */}
      <section className="pg-plans">
        <div className="pg-container">
          <h2 className="pg-section-title">Choose Your Plan</h2>
          <p className="pg-section-sub">Switch between monthly and annual billing anytime. Cancel anytime.</p>

          <div className="pg-trial-banner">
            <i className="bi bi-gift-fill" />
            <span>All plans include a <strong>14-day free trial</strong> — full access, no charge until your trial ends.</span>
          </div>

          <div className="pg-billing-toggle">
            <button
              className={`pg-billing-btn ${billing === 'monthly' ? 'active' : ''}`}
              onClick={() => setBilling('monthly')}
            >
              Monthly
            </button>
            <button
              className={`pg-billing-btn ${billing === 'annual' ? 'active' : ''}`}
              onClick={() => setBilling('annual')}
            >
              Annual
            </button>
          </div>

          <div className="pg-plan-grid">
            {PLANS.map((plan) => {
              const priceInfo = billing === 'annual' ? plan.annual : plan.monthly;
              return (
                <div
                  key={plan.key}
                  className={`pg-plan-card ${plan.key === 'workplace' ? 'pg-plan-card--workplace' : ''}`}
                >
                  <div className="pg-plan-card-icon"><i className={`bi ${plan.icon}`} /></div>
                  <h3 className="pg-plan-card-title">{plan.title}</h3>
                  <p className="pg-plan-card-sub">{plan.subtitle}</p>
                  <div className="pg-plan-card-price">
                    <span className="pg-price-amount">{priceInfo.price}</span>
                    <span className="pg-price-period">{priceInfo.label}</span>
                  </div>
                  {billing === 'annual' && (
                    <div className="pg-price-savings">{priceInfo.savings}</div>
                  )}
                  <ul className="pg-plan-features">
                    {plan.features.map((f) => (
                      <li key={f}><i className="bi bi-check2" />{f}</li>
                    ))}
                  </ul>
                  <button
                    className="pg-plan-cta"
                    onClick={() => handlePlanCTA(plan.key)}
                  >
                    {auth.isAuthenticated ? 'Subscribe Now' : 'Start Free Trial'}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="pg-plans-note">
            Subscriptions are managed through Lemon Squeezy. You can cancel anytime from your
            billing portal.
          </p>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────── */}
      {!auth.isAuthenticated && (
        <section className="pg-how">
          <div className="pg-container">
            <h2 className="pg-section-title">How to get started</h2>
            <div className="pg-steps">
              <div className="pg-step">
                <div className="pg-step-num">1</div>
                <h4 className="pg-step-title">Choose a plan</h4>
                <p className="pg-step-desc">Select Individual or Workspace above. Annual billing saves 15%.</p>
              </div>
              <div className="pg-step-arrow"><i className="bi bi-arrow-right" /></div>
              <div className="pg-step">
                <div className="pg-step-num">2</div>
                <h4 className="pg-step-title">Create your account</h4>
                <p className="pg-step-desc">Register with your name, work email, and a password — takes under a minute.</p>
              </div>
              <div className="pg-step-arrow"><i className="bi bi-arrow-right" /></div>
              <div className="pg-step">
                <div className="pg-step-num">3</div>
                <h4 className="pg-step-title">Start your free trial</h4>
                <p className="pg-step-desc">Complete checkout and get immediate full access — free for 14 days, then your plan rate.</p>
              </div>
            </div>
            <div className="pg-how-cta">
              <button className="pg-how-btn" onClick={() => handlePlanCTA('individual')}>
                Start Free Trial
              </button>
              <p className="pg-how-note">
                Already have an account?{' '}
                <button className="pg-auth-link" data-bs-toggle="modal" data-bs-target="#loginModal">
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </section>
      )}

      <footer className="pg-footer">
        <span>&copy; <i>'ello</i>Govna {new Date().getFullYear()}</span>
        <a
          className="pg-footer-social"
          href="https://www.linkedin.com/company/ellogovna/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="'elloGovna on LinkedIn"
        >
          <i className="bi bi-linkedin" />
        </a>
      </footer>

      <LoginModal />
    </>
  );
}
